import React, { FC, useMemo, useRef, useState } from "react";
import { Col, Table, Row, Button, Input, Space, Tooltip } from "antd";
import CustomTooltip from "@pages/tool-tip";
import { SearchOutlined } from "@ant-design/icons";
import { map, cloneDeep, get, filter } from "lodash";
import Highlighter from "react-highlight-words";
// import { formatDateStandard } from "@pages/utils/utils";
import { centricClientCheck, fixedDecimal, formatDisplayDate } from "@utils/helpers";
import { TableLocale } from "@components/noDataFound";
import Icon from "@assets/icon_config";
import MultiSelectColFilters from "@components/filters/multiSelectColFilters";
import memberDetailsService from "@services/memberDetails.service";
import membersService from "@services/members.service";
import { FilterValue } from "antd/lib/table/interface";
import { TableProps } from "antd/es/table";

const Drg: FC<any> = (props) => {
  const isCentric = centricClientCheck();
  const membersServiceGetCurrentMember = membersService?.getCurrentMember;
  const patientId = useMemo(() => membersServiceGetCurrentMember?.patient_id, [membersServiceGetCurrentMember?.patient_id]);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});

  let tableColumns = [
    {
      title: "ADMISSION DATE",
      dataIndex: "date",
      // width: "15%",
      ellipsis: {
        showTitle: false,
      },
      render: (date: any) => {
        const formattedDate = formatDisplayDate(date, "reminderDateActualFormat", "");
        return <CustomTooltip toolTipvalue={formattedDate} />;
      },
      sorter: (a: any, b: any) => {
        return a.date?.localeCompare(b.date);
      },
    },
    {
      title: "DISCHARGE DATE",
      dataIndex: "hospital_admission_end_date",
      // width: "15%",
      ellipsis: {
        showTitle: false,
      },
      render: (hospital_admission_end_date: any) => {
        const formattedDate = formatDisplayDate(hospital_admission_end_date, "reminderDateActualFormat", "");
        return <CustomTooltip toolTipvalue={formattedDate} />;
      },
      sorter: (a: any, b: any) => {
        return a.hospital_admission_end_date?.localeCompare(b.hospital_admission_end_date);
      },
    },
    {
      title: "DATE",
      dataIndex: "date",
      // width: "15%",
      ellipsis: {
        showTitle: false,
      },
      render: (date: any) => {
        const formattedDate = formatDisplayDate(date, "reminderDateActualFormat", "");
        return <CustomTooltip toolTipvalue={formattedDate} />;
      },
      sorter: (a: any, b: any) => {
        return a.date?.localeCompare(b.date);
      },
    },
    {
      title: "CLAIM SERVICE TYPE",
      dataIndex: "claimServiceType",
      ellipsis: {
        showTitle: false,
      },
      render: (claimServiceType: any) => {
        return <CustomTooltip toolTipvalue={claimServiceType} />;
      },
      sorter: (a: any, b: any) => {
        return a.claimServiceType?.localeCompare(b.claimServiceType);
      },
    },
    {
      title: "DRG CODE",
      dataIndex: "drgCode",
      // width: "15%",
      ellipsis: {
        showTitle: false,
      },
      render: (drgCode: any) => <CustomTooltip toolTipvalue={drgCode} />,
      sorter: (a: any, b: any) => {
        return a.drgCode?.localeCompare(b.drgCode);
      },
    },
    {
      title: "COST",
      dataIndex: "cost",
      // width: "15%",
      ellipsis: {
        showTitle: false,
      },
      className: !centricClientCheck() && "textAlignRight",
      render: (cost: any, data: any) => {
        const children = (
          <div>
            <span style={{ fontWeight: "bold", marginRight: "8px" }}>$ </span>
            {`${fixedDecimal(cost, 2)} `}
          </div>
        );
        return <CustomTooltip toolTipvalue={children} />;
      },
      sorter: (a: any, b: any) => {
        return a.cost - b.cost;
      },
    },
    {
      title: "DESCRIPTION",
      dataIndex: "drgDesc",
      ellipsis: {
        showTitle: false,
      },
      render: (drgDesc: any) => <CustomTooltip toolTipvalue={drgDesc} />,
      sorter: (a: any, b: any) => {
        return a.drgDesc?.localeCompare(b.drgDesc);
      },
    },
  ];

  tableColumns = filter(tableColumns, (item) => {
    if (isCentric) {
      return item.title !== "DATE";
    } else {
      return item.title !== "ADMISSION DATE" && item.dataIndex !== "hospital_admission_end_date" && item.dataIndex !== "claimServiceType";
    }
  });

  const data = props?.data;

  /**
   * Column search functionality
   *
   */
  const [tableData, setTableData] = useState(data);
  const [columnSearchInfo, setColumnSearchInfo] = useState({});
  const [admissionDateFilterList, setAdmissionDateFilterList] = useState([]);
  const [drgCodeFilterList, setDRGCodeFilterList] = useState([]);

  const fetchColumnFilterList = (attributeName: string, payload: any) => {
    memberDetailsService.fetchAttributeDropdownWithSqParams({ attributeName, sqlParamsFromPayload: [payload] }).subscribe({
      next: (response: any) => {
        const formattedData =
          response?.data?.data?.data.map((date: any) => ({
            label: formatDisplayDate(date.label, "reminderDateActualFormat", ""),
            value: formatDisplayDate(date.value, "reminderDateActualFormat", ""),
          })) || [];

        if (attributeName === "AdmissionDateFilterLst") {
          setAdmissionDateFilterList(formattedData);
        } else if (attributeName === "DRGCodeLst") {
          setDRGCodeFilterList(response?.data?.data?.data);
        }
      },
      error: (error) => console.error(error),
    });
  };

  React.useEffect(() => {
    setTableData(data);
    if(isCentric){
      fetchColumnFilterList('DRGCodeLst', patientId); 
      fetchColumnFilterList('AdmissionDateFilterLst', patientId);
    }
  }, [data]);
  
  const handleChange: TableProps<any>['onChange'] = (_, filters, __) => {
    setFilteredInfo(filters);
  };

  const highlighterDiv = (text: any, searchWord: any) => {
    return (
      <Tooltip placement="topLeft" title={text ? text.toString() : ""}>
        <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[searchWord]} autoEscape textToHighlight={text ? text.toString() : ""} />
      </Tooltip>
    );
  };

  const applyColumnSearch = (columnName: any, text: any) => {
    text = text ? text : get(columnSearchInfo, [columnName], "");
    if (text !== "") {
      let tempMedicationsData = data.filter((data: any) => {
        if (data[columnName]) {
          return data[columnName].toString().toLowerCase().includes(text.toLowerCase());
        } else {
          return false;
        }
      });
      setTableData(tempMedicationsData);
    } else {
      setTableData(data);
    }
  };

  const inputref = useRef<any>(null);

  const _xyz: Array<{}> = map(cloneDeep(tableColumns), (dataInfo: any) => {
    return {
      ...dataInfo,

      filterDropdown:
        (dataInfo.dataIndex === "drgCode" && isCentric) || dataInfo.title === "ADMISSION DATE"
          ? ({ setSelectedKeys, selectedKeys, confirm, visible }: any) => (
              <MultiSelectColFilters
                filterDropdowns={dataInfo.dataIndex === "drgCode" ? drgCodeFilterList : admissionDateFilterList}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
                confirm={confirm}
                placeholder={dataInfo?.title.toLowerCase().split(" ").map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")}
                visible={visible}
                setFilteredInfo={setFilteredInfo}
              />
            )
          : ({ confirm }: any) => (
              <div style={{ padding: "10px" }}>
                <Input
                  style={{ marginBottom: 8, display: "block" }}
                  value={get(columnSearchInfo, dataInfo.dataIndex, "")}
                  ref={inputref}
                  onChange={(e) => {
                    setColumnSearchInfo({
                      ...columnSearchInfo,
                      [dataInfo.dataIndex]: e.target.value,
                    });
                  }}
                  onPressEnter={(e: any): any => {
                    applyColumnSearch(dataInfo.dataIndex, "");
                    confirm({ closeDropdown: true });
                    e.stopPropagation();
                  }}
                />
                <Space>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90 }}
                    onClick={(e) => {
                      setColumnSearchInfo({
                        [dataInfo.dataIndex]: get(columnSearchInfo, dataInfo.dataIndex, ""),
                      });
                      applyColumnSearch(dataInfo.dataIndex, "");
                      confirm({ closeDropdown: true });
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    onClick={() => {
                      setColumnSearchInfo({ [dataInfo.dataIndex]: "" });
                      applyColumnSearch("", "");
                      confirm({ closeDropdown: true });
                    }}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Reset
                  </Button>
                </Space>
              </div>
            ),
      filteredValue: filteredInfo[dataInfo.dataIndex] || null,
      filterIcon: (value: any) =>
        dataInfo.dataIndex === "action" ? null : (dataInfo.dataIndex === "drgCode" && isCentric) || dataInfo.title === "ADMISSION DATE" ? (
          <Icon name="funnel" colorOfBrand={value ? "#ef6d2a" : ""} />
        ) : (
          <SearchOutlined
            style={{
              color: get(columnSearchInfo, dataInfo.dataIndex, "") !== "" ? "#EF6D2A" : undefined,
            }}
          />
        ),
      render:
        dataInfo.dataIndex === "date" || dataInfo.dataIndex === "hospital_admission_end_date"
          ? dataInfo.render
          : dataInfo.dataIndex === "cost"
          ? (text: any, record: any) => highlighterDiv("$ " + fixedDecimal(text, 2), get(columnSearchInfo, dataInfo.dataIndex, ""))
          : (text: any, record: any) => highlighterDiv(text, get(columnSearchInfo, dataInfo.dataIndex, "")),
      onFilter: (value: any, record: any) => {
        if (dataInfo.title === "ADMISSION DATE") {
          const formattedDate = formatDisplayDate(record[dataInfo.dataIndex], "reminderDateActualFormat", "");
          return formattedDate.includes(value);
        }
        return record[dataInfo.dataIndex] ? record[dataInfo.dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : "";
      },
      onFilterDropdownOpenChange: (visible: any) => {
        if (visible) {
          setTimeout(() => inputref.current?.select(), 100);
        }
      },
    };
  });

  return (
    // <div>
    // {/* <Row style={{margin:"16px 0"}}>
    //   <Col span={24}>
    //     <span className="labelText clr-n100">DRG</span>
    //   </Col>
    // </Row> */}
    <Row>
      <Col span={24}>
        <Table
          bordered={true}
          pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ["5", "10", "15", "20", "25", "30"] }}
          columns={_xyz}
          dataSource={tableData}
          className="table-rows"
          locale={TableLocale}
          onChange={handleChange}
        />
      </Col>
    </Row>
    // </div>
  );
};

export default Drg;
